import type { FormRecordFlag, RecordStatusView, ShipTime } from '@felix/domain';
import type { TFunction } from 'i18next';
import { formatDateTime, type Instant } from '../../format.js';
import type { StatusTone } from '../../StatusBadge.js';
import { vesselOffsetMinutes } from '../documents/library.js';
import { FORM_RECORD_ERROR_CODES } from './error-codes.js';
import type { RecordUploadProgress } from './source.js';

/**
 * Kayıt durumunun ve işaretlerinin gösterimi (plan M8b Task 15; ADR 0042 §4, §13, §16, §20).
 * Durum saklanmaz, okuma modelinden gelir; burada yalnız metin ve ton seçilir. Renkler token
 * tonlarından (`StatusBadge`; docs/ekranlar/README.md "Tasarım değerleri").
 */

export interface RecordBadge {
  key: string;
  tone: StatusTone;
  label: string;
}

type T = TFunction | ((key: string, options?: Record<string, unknown>) => string);

const STATUS_TONE: Record<RecordStatusView['status'], StatusTone> = {
  draft: 'info',
  discarded: 'neutral',
  pending: 'warning',
  returned: 'danger',
  approved: 'success',
  void: 'danger',
  inconsistent: 'danger',
};

/** Durumun tek satırlık adı: "Pending (step 1 of 2)", ofis adımında "Pending office (…)". */
export function recordStatusLabel(t: T, status: RecordStatusView): string {
  if (status.status === 'pending') {
    const values = { step: status.step ?? 1, steps: status.steps ?? 1 };
    return t(status.office ? 'forms.status.pendingOffice' : 'forms.status.pendingStep', values);
  }
  return t(`forms.status.${status.status}`);
}

export function recordStatusTone(status: RecordStatusView): StatusTone {
  return STATUS_TONE[status.status];
}

/**
 * Eski revizyon işaretinin tonu (ADR 0042 §4): `received_but_superseded` saat kayması ya da
 * hatadır (ofis uyarısı); diğerleri dikkat işaretidir.
 */
export function revisionFlagTone(flag: FormRecordFlag): StatusTone {
  return flag === 'received_but_superseded' ? 'danger' : 'warning';
}

export function flagLabel(t: T, flag: FormRecordFlag): string {
  return t(`forms.flags.${flag}`);
}

/**
 * Durum rozeti, değiştirildiyse "Amended (rev. n)" (mimari belge §7.4; ADR 0042 §20) ve eski
 * revizyon işaretleri [sorular #229, #238]. Sıra: durum, Amended, işaretler.
 */
export function recordStatusBadges(
  t: T,
  status: RecordStatusView,
  flags: readonly FormRecordFlag[],
): RecordBadge[] {
  const badges: RecordBadge[] = [
    { key: 'status', tone: recordStatusTone(status), label: recordStatusLabel(t, status) },
  ];
  if (status.amended) {
    badges.push({
      key: 'amended',
      tone: 'info',
      label: t('forms.status.amended', { revision: status.revisionNo ?? 1 }),
    });
  }
  for (const flag of flags) {
    badges.push({ key: flag, tone: revisionFlagTone(flag), label: flagLabel(t, flag) });
  }
  return badges;
}

const pad = (value: number) => String(value).padStart(2, '0');

/** "UTC+03:00", "UTC−05:00" (eksi işareti), farksız "UTC±00:00". */
export function utcOffsetLabel(offsetMin: number): string {
  if (offsetMin === 0) return 'UTC±00:00';
  const sign = offsetMin > 0 ? '+' : '−';
  const abs = Math.abs(offsetMin);
  return `UTC${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/**
 * Kayıt anı: gemide gemi saati ve UTC farkı ("10.10.2026 14:32 (UTC+03:00)"), ofiste UTC
 * ("10.10.2026 11:32 UTC") (ADR 0042 §16; CLAUDE.md "Zaman"). Fark anın kendisine göredir (yaz
 * saati geçişi).
 */
export function recordTime(at: Instant | null | undefined, timeZone: ShipTime | null): string {
  if (at === null || at === undefined || at === '') return '—';
  if (timeZone === null) {
    const text = formatDateTime(at, 0);
    return text === '—' ? text : `${text} UTC`;
  }
  const offset = vesselOffsetMinutes(timeZone, at instanceof Date ? at : new Date(at));
  const text = formatDateTime(at, offset);
  return text === '—' ? text : `${text} (${utcOffsetLabel(offset)})`;
}

/** Hata kodunun metni; bilinmeyen kod genel metne düşer. */
export function formErrorText(t: T, error: unknown): string {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code: unknown }).code)
      : error instanceof Error
        ? error.message
        : '';
  return (FORM_RECORD_ERROR_CODES as readonly string[]).includes(code)
    ? t(`forms.errors.${code}`)
    : t('forms.errors.GENERIC');
}

/**
 * Çoklu seçimde reddedilen dosyalar: adı ve nedeniyle tek uyarı (M14b; ADR 0055 §3)
 * [sorular #342]; yoksa `null`.
 */
export function rejectedFilesText(
  t: T,
  failed: readonly { name: string; code: string }[],
): string | null {
  if (failed.length === 0) return null;
  const list = failed
    .map((file) => `${file.name} – ${formErrorText(t, { code: file.code })}`)
    .join(' ');
  return t('forms.files.rejected', { list });
}

/** Yükleme yüzdesi (0–100, tam sayı). */
export function uploadProgressPercent(progress: RecordUploadProgress): number {
  if (progress.total <= 0) return 0;
  return Math.min(100, Math.floor((progress.sent / progress.total) * 100));
}

/** "Uploading tarama.pdf (2 of 3) – 25%" (ADR 0055 §4) [sorular #343]. */
export function uploadProgressText(t: T, progress: RecordUploadProgress): string {
  return t('forms.files.uploadingProgress', {
    name: progress.name,
    index: progress.index,
    count: progress.count,
    percent: uploadProgressPercent(progress),
  });
}

/** Dosya boyutu dile göre ("1,2 MB" / "1.2 MB"). */
export function fileSizeText(bytes: number, locale: string): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = unit === 0 || value >= 100 ? 0 : 1;
  return `${new Intl.NumberFormat(locale === 'en' ? 'en-GB' : locale, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(value)} ${units[unit]}`;
}

/** SHA-256'nın ilk 12 hanesi (kapak ve ekranda; MEPC.312 4.3.5). */
export const shortHash = (hash: string | null | undefined): string =>
  hash ? hash.slice(0, 12) : '—';
